import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { useGetPublicStatisticsQuery, useGetPublicProjectsQuery } from '../store/slices/apiSlice';
import {
    Users,
    Target,
    Award,
    TrendingUp,
    Shield,
    Globe,
    Heart,
    CheckCircle,
    ArrowRight,
    Building2,
    DollarSign,
    Clock,
    MapPin,
    Phone,
    Mail,
    Linkedin,
    Twitter,
    Sparkles,
    Eye,
    Bookmark,
    MessageCircle,
    Quote,
    Star,
    Briefcase,
    Lightbulb,
    Handshake,
    BarChart3
} from 'lucide-react';

import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import FAQ from '../components/common/FAQ';

const About = () => {
    const [isVisible, setIsVisible] = useState(false);

    // Set page metadata
    usePageMetadata('about');

    // Fetch real statistics
    const { data: statsData } = useGetPublicStatisticsQuery();
    const { data: projectsResponse } = useGetPublicProjectsQuery();
    const projects = Array.isArray(projectsResponse?.data) ? projectsResponse?.data :
        Array.isArray(projectsResponse?.data?.projects) ? projectsResponse?.data?.projects : [];

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const formatCurrency = (amount: number) => {
        return `$${amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    const formatNumber = (num: number) => {
        return num.toLocaleString();
    };

    const totalInvestment = projects.reduce((sum: number, project: Record<string, unknown>) => {
        const amountRaised = parseFloat((project.amount_raised as string) || (project.original_amount_raised as string) || '0');
        return sum + amountRaised;
    }, 0);

    const totalROI = projects.reduce((sum: number, project: Record<string, unknown>) => {
        const roi = parseFloat((project.expected_roi_percentage as string) || '0');
        return sum + roi;
    }, 0);
    const averageROI = projects.length > 0 ? (totalROI / projects.length).toFixed(1) : '0';

    const projectsFunded = projects.filter((project: Record<string, unknown>) => {
        const isFunded = project.is_funded === true;
        const fundingGoal = parseFloat((project.funding_goal as string) || (project.original_funding_goal as string) || '0');
        const amountRaised = parseFloat((project.amount_raised as string) || (project.original_amount_raised as string) || '0');
        return isFunded || (fundingGoal > 0 && amountRaised >= fundingGoal);
    }).length;

    const totalUsers = statsData?.data?.total_users || 0;
    const totalAmountInvested = statsData?.data?.total_amount_invested || totalInvestment;

    // Core Values
    const values = [
        {
            icon: Shield,
            title: "Trust & Security",
            description: "Bank-grade security with complete transparency in all transactions across borders",
            color: "from-green-500 to-emerald-600"
        },
        {
            icon: Target,
            title: "Excellence",
            description: "Delivering exceptional returns through carefully curated Nigerian investment opportunities",
            color: "from-blue-500 to-cyan-600"
        },
        {
            icon: Heart,
            title: "Community",
            description: "Building a global community of diaspora investors connected to their homeland",
            color: "from-purple-500 to-violet-600"
        },
        {
            icon: Globe,
            title: "Innovation",
            description: "Leveraging cutting-edge technology to bridge continents and create lasting impact",
            color: "from-orange-500 to-amber-600"
        }
    ];

    // Journey milestones
    const milestones = [
        {
            year: "2020",
            title: "The Vision Begins",
            description: "Diasporawise was founded with a bold vision — to connect the Nigerian diaspora with safe, transparent, and profitable investment opportunities back home."
        },
        {
            year: "2021",
            title: "Platform Launch",
            description: "Our digital investment platform went live, enabling diaspora Nigerians to invest in vetted real estate projects from anywhere in the world."
        },
        {
            year: "2023",
            title: "Scaling Impact",
            description: "Expanded our portfolio to include commercial properties, renewable energy, and agricultural ventures across Nigeria's high-growth regions."
        },
        {
            year: "2025",
            title: "Global Reach",
            description: "Serving investors across the US, UK, Europe, and Asia — with a mobile app, advanced analytics, and a growing community of empowered investors."
        }
    ];

    return (
        <div className="min-h-screen bg-white">
            <Navbar />

            {/* Hero Section */}
            <section className="relative py-16 md:py-24 px-6 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0 overflow-hidden">
                    <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto max-w-7xl relative z-10">
                    <div className={`text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6 text-blue-200 border border-white/10">
                            <Sparkles className="w-4 h-4" />
                            About Diasporawise
                        </div>

                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Empowering the Diaspora to
                            <span className="block text-blue-300">Invest in Nigeria's Future</span>
                        </h1>

                        <p className="text-lg md:text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
                            We bridge the gap between Nigerian diaspora investors and premium investment
                            opportunities back home — with transparency, security, and exceptional returns.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/projects"
                                className="group bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-blue-50 transition-all duration-300 flex items-center justify-center gap-3 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                            >
                                Explore Projects
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                            </Link>
                            <Link
                                to="/contact"
                                className="group border border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300 flex items-center justify-center gap-3 backdrop-blur-sm"
                            >
                                <MessageCircle className="w-5 h-5" />
                                Get in Touch
                            </Link>
                        </div>
                    </div>
                </div>
            </section>



            {/* CEO Message Section */}
            <section className="py-20 px-6 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                        {/* CEO Image */}
                        <div className="relative order-2 lg:order-1">
                            <div className="relative max-w-md mx-auto lg:mx-0">
                                {/* Decorative background glow */}
                                <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-3xl blur-xl" />
                                <div className="relative">
                                    <img
                                        src="/ceo-new.jpg"
                                        alt="CEO of Diasporawise"
                                        className="rounded-2xl shadow-2xl w-full h-[500px] object-cover object-top border-2 border-white/20"
                                    />
                                </div>
                                {/* Decorative badge */}
                                <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 bg-white px-6 py-3 rounded-2xl shadow-xl border border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                                            <Briefcase className="w-5 h-5 text-white" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-900 text-sm">Femi Adewumi</p>
                                            <p className="text-xs text-gray-500">Founder & CEO, Diasporawise</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* CEO Message */}
                        <div className="order-1 lg:order-2">
                            <div className="inline-flex items-center gap-2 bg-blue-500/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-blue-300 mb-6 border border-blue-500/30">
                                <Quote className="w-4 h-4" />
                                Message from the CEO
                            </div>

                            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                                Building Bridges Between
                                <span className="text-blue-400"> Dreams & Homeland</span>
                            </h2>

                            <div className="space-y-4 text-gray-300 leading-relaxed">
                                <p className="text-lg">
                                    "As a Nigerian living abroad, I experienced firsthand the frustration of wanting to invest
                                    back home but facing barriers at every turn — lack of transparency, unreliable intermediaries,
                                    and complex processes that made it nearly impossible."
                                </p>

                                <p>
                                    "That's why I founded Diasporawise. Our mission is simple yet powerful: to give every
                                    Nigerian in the diaspora a safe, transparent, and profitable way to invest in their homeland.
                                    We believe that when the diaspora invests in Nigeria, everyone wins — families grow stronger,
                                    communities flourish, and the nation prospers."
                                </p>

                                <p>
                                    "Every project on our platform is personally vetted by our team. We don't just offer
                                    investment opportunities — we offer peace of mind. Your trust is our foundation,
                                    and your success is our greatest achievement."
                                </p>
                            </div>

                        </div>
                    </div>
                </div>
            </section>

            {/* Our Story Section */}
            <section className="py-20 px-6 bg-white">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-500 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                            <Bookmark className="w-4 h-4" />
                            Our Story
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            The Journey So Far
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From a bold idea to a trusted platform — here's how Diasporawise is transforming
                            diaspora investment in Nigeria.
                        </p>
                    </div>

                    {/* Timeline */}
                    <div className="relative">
                        {/* Timeline line */}
                        <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-blue-400 to-blue-600" />

                        <div className="space-y-12 lg:space-y-0">
                            {milestones.map((milestone, index) => (
                                <div key={index} className={`lg:flex items-center gap-8 lg:mb-16 ${index % 2 === 0 ? '' : 'lg:flex-row-reverse'}`}>
                                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                                        <div className={`bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 ${index % 2 === 0 ? 'lg:ml-auto' : 'lg:mr-auto'} max-w-lg`}>
                                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-bold mb-4" style={{ backgroundColor: '#e8f0fe', color: '#1a73e8' }}>
                                                <Clock className="w-3 h-3" />
                                                {milestone.year}
                                            </div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-3">{milestone.title}</h3>
                                            <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                                        </div>
                                    </div>

                                    {/* Timeline dot */}
                                    <div className="hidden lg:flex items-center justify-center w-12 h-12 rounded-full shadow-lg border-4 border-white flex-shrink-0 z-10" style={{ background: 'linear-gradient(135deg, #1a73e8, #1557b0)' }}>
                                        <span className="text-white text-xs font-bold">{milestone.year.slice(2)}</span>
                                    </div>

                                    <div className="flex-1 hidden lg:block" />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Values Section */}
            <section className="py-20 px-6 bg-gray-50">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-600 to-purple-500 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                            <Target className="w-4 h-4" />
                            Mission & Values
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            What Drives Us Forward
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Our mission and values are the foundation of everything we do — empowering diaspora
                            Nigerians to build wealth and impact through trusted investments.
                        </p>
                    </div>

                    {/* Vision & Mission Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
                        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-10 text-white shadow-2xl">
                            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                                <Eye className="w-7 h-7 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                            <p className="text-blue-100 leading-relaxed text-lg">
                                To become the most trusted investment platform for the global Nigerian diaspora,
                                creating a seamless bridge between international capital and Nigeria's most
                                promising growth opportunities.
                            </p>
                        </div>

                        <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl p-10 text-white shadow-2xl">
                            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mb-6">
                                <Lightbulb className="w-7 h-7 text-white" />
                            </div>
                            <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                            <p className="text-purple-100 leading-relaxed text-lg">
                                To democratize investment opportunities by providing transparent, secure, and
                                accessible investments that enable Nigerians worldwide to build wealth
                                and contribute to their homeland's prosperity.
                            </p>
                        </div>
                    </div>

                    {/* Values Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => {
                            const IconComponent = value.icon;
                            return (
                                <div
                                    key={index}
                                    className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-4 cursor-pointer border border-gray-100"
                                    style={{ animationDelay: `${index * 150}ms` }}
                                >
                                    <div className={`w-16 h-16 bg-gradient-to-br ${value.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                        <IconComponent className="w-8 h-8 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-blue-600 transition-colors duration-300">
                                        {value.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        {value.description}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Why Diasporawise Section */}
            <section className="py-20 px-6 bg-white">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-500 px-4 py-2 rounded-full text-sm font-semibold text-white mb-6">
                            <Award className="w-4 h-4" />
                            Why Choose Us
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            Why Investors Trust Diasporawise
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {[
                            { icon: Shield, title: "Vetted Opportunities", desc: "Every project undergoes rigorous due diligence before listing — legal, financial, and on-ground verification." },
                            { icon: BarChart3, title: "Real-Time Tracking", desc: "Monitor your investments with live dashboards, progress updates, and transparent financial reporting." },
                            { icon: Handshake, title: "Dedicated Support", desc: "Our team provides personalized guidance throughout your investment journey, from decision to returns." },
                            { icon: Globe, title: "Invest from Anywhere", desc: "Whether you're in the US, UK, Europe, or Asia — our platform gives you seamless access to Nigerian markets." },
                            { icon: DollarSign, title: "Competitive Returns", desc: "Our curated projects offer attractive ROI with structured payment plans including installment options." },
                            { icon: Award, title: "Proven Track Record", desc: `${formatCurrency(totalAmountInvested)} invested and ${projectsFunded} projects funded — backed by a growing community of satisfied investors.` }
                        ].map((item, index) => {
                            const Icon = item.icon;
                            return (
                                <div key={index} className="group bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                        <Icon className="w-7 h-7 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">{item.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{item.desc}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-20 px-6 bg-gray-50">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <div className="inline-flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-full text-sm font-medium mb-6 text-gray-600 border border-gray-200">
                                <MessageCircle className="w-4 h-4" />
                                Get in Touch
                            </div>

                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                                Let's Build Something
                                <span className="block text-gray-700">Amazing Together</span>
                            </h2>

                            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                Ready to start your investment journey? Our team is here to help you every step of the way.
                                Get in touch with us today.
                            </p>

                            <div className="space-y-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center">
                                        <Mail className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">Email Us</p>
                                        <p className="text-gray-600">info@diasporawise.com</p>
                                    </div>
                                </div>

                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gray-800 rounded-xl flex items-center justify-center">
                                        <Globe className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <p className="font-semibold text-gray-900">Global Presence</p>
                                        <p className="text-gray-600">Serving investors in US, UK, Europe & Asia</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <FAQ
                title="About Diasporawise - Common Questions"
                subtitle="Learn more about our company, mission, and how we're revolutionizing diaspora investment"
                faqs={[
                    {
                        id: 1,
                        question: "What is Diasporawise and what makes it different?",
                        answer: "Diasporawise is a premium investment platform purpose-built for the Nigerian diaspora. We differentiate ourselves through rigorous project vetting, complete transparency, real-time tracking, and a deep understanding of the challenges diaspora investors face when investing back home."
                    },
                    {
                        id: 2,
                        question: "How does Diasporawise vet investment opportunities?",
                        answer: "Every project undergoes a multi-stage vetting process including financial analysis, legal compliance checks, on-ground site verification, and market potential assessment. Our expert team personally evaluates each opportunity before it's made available to investors."
                    },
                    {
                        id: 3,
                        question: "What is the company's mission?",
                        answer: "Our mission is to democratize access to premium Nigerian investment opportunities while maintaining the highest standards of security, transparency, and customer service — enabling diaspora Nigerians to build wealth and contribute to their homeland's growth."
                    },
                    {
                        id: 4,
                        question: "Can I invest from outside Nigeria?",
                        answer: "Absolutely! Diasporawise was specifically designed for the Nigerian diaspora. Whether you're in the US, UK, Europe, or Asia, our platform provides seamless access to vetted Nigerian investment opportunities with secure cross-border payment processing."
                    },
                    {
                        id: 5,
                        question: "How do I track my investments?",
                        answer: "Our platform provides real-time dashboards with detailed portfolio tracking, project progress updates, financial reports, and instant notifications. You can also access everything via our mobile app available on iOS and Android."
                    },
                    {
                        id: 6,
                        question: "What support does Diasporawise provide?",
                        answer: `We provide comprehensive support including a dedicated customer service team, personalized investment guidance, educational resources, and 24/7 platform access. With ${formatNumber(totalUsers)}+ active investors, our growing community is testament to our commitment to excellence.`
                    }
                ]}
            />

            <Footer />
        </div>
    );
};

export default About;